import type {ReactElement} from 'react';
import React, {useState, useEffect, useRef} from 'react';
import useWindowSize from '@Hooks/useWindowSize';
import {v4 as uuidv4} from 'uuid';
import {ImageAndVideoRecorderStatus, RecorderMediaType} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import {isOpenCustomImageAndVideoRecorderSmall} from '@Libraries/record-image-and-video.library';
import {isIOS, isSafari} from 'react-device-detect';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {CameraAndMicrophonePermission} from '@Panels/record-image-and-video-panel/components/camera-and-microphone-permission';
import {PanelContent, PanelFooterPrimaryDefaultAction} from '@Components/panel';
import {Type} from '@Components/button';
import {ImageAndVideoRecorderControls} from '@Panels/record-image-and-video-panel/components/image-and-video-recorder-controls';
import {ImageAndVideoToggleMobile} from '@Panels/record-image-and-video-panel/components/image-and-video-toggle-mobile';
import {setSelectedCamera} from '@Panels/record-image-and-video-panel/record-image-and-video-panel-reducer';
import {ImageAndVideoRecorderFeed} from '@Panels/record-image-and-video-panel/components/image-and-video-recorder-feed';
import styles from './record-image-and-video-panel-content.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

export interface RecordImageAndVideoPanelContentProps {
  panelId?: string;
  checkSystemPermissionsError: boolean;
  permissionsDeniedError: boolean;
  rempromptForPermissions: boolean;
  showMaxDurationError: boolean;
  recordingDuration: number;
  stream: MediaStream | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  videoInputDevices: Array<string>;
  onRetake(): void;
  onClose?(): void;
  setAllVideoInputDeviceIds(): Promise<void>;
  getCamera(): Promise<void>;
  onAddToDesign(): void;
  customOnClose(): void;
  captureImage(): void;
  startRecordingVideo(): void;
  stopRecordingVideo(): void;
  hideToggle?: boolean;
}

export function RecordImageAndVideoPanelContent({
  getCamera = async (): Promise<void> => {},
  setAllVideoInputDeviceIds = async (): Promise<void> => {},
  captureImage = (): void => {},
  startRecordingVideo = (): void => {},
  stopRecordingVideo = (): void => {},
  onAddToDesign = (): void => {},
  customOnClose = (): void => {},
  hideToggle = false,
  ...props
}: RecordImageAndVideoPanelContentProps): ReactElement | null {
  const dispatch = useAppDispatch();

  const recorderStatus = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.imageAndVideoRecorderStatus;
  });

  const mediaType = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.mediaType;
  });

  const panelId = useRef(props.panelId ?? uuidv4());
  const {windowWidth} = useWindowSize();

  const [showIosPlaybackVideoPreloader, setShowIosPlaybackVideoPreloader] = useState(
    isIOS && recorderStatus === ImageAndVideoRecorderStatus.STOPPED && mediaType === RecorderMediaType.VIDEO
  );

  useEffect(() => {
    setShowIosPlaybackVideoPreloader(isIOS && recorderStatus === ImageAndVideoRecorderStatus.STOPPED && mediaType === RecorderMediaType.VIDEO);
  }, [recorderStatus, mediaType]);

  useEffect(() => {
    dispatch(setSelectedCamera({selectedCamera: props.videoInputDevices[0]}));
  }, []);

  const getMaxDurationErrorMessage = (): ReactElement => {
    return (
      <div className={`${styles.messageContainer} flex-row-justify-center flex-items-center`}>
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_20} />
        <Text val={window.i18next.t('pmwjs_video_duration_limit_reached')} size={TextSize.XSMALL} />
      </div>
    );
  };

  const getPanelContentForGeneralPanel = (): ReactElement | null => {
    if (props.permissionsDeniedError || props.rempromptForPermissions || props.checkSystemPermissionsError) {
      return (
        <CameraAndMicrophonePermission
          panelId={panelId.current}
          rempromptForPermissions={props.rempromptForPermissions}
          checkSystemPermissionsError={props.checkSystemPermissionsError}
          permissionsDeniedError={props.permissionsDeniedError}
          onReprompt={isSafari ? setAllVideoInputDeviceIds : getCamera}
        />
      );
    }

    return (
      <div className={`${styles.content}`}>
        {props.showMaxDurationError ? getMaxDurationErrorMessage() : null}
        <ImageAndVideoRecorderFeed
          showIosPlaybackVideoPreloader={false}
          className={props.showMaxDurationError ? `spacing-m-t-8 ${styles.recorderWithDurationError}` : ''}
          duration={props.recordingDuration}
          stream={props.stream}
          videoRef={props.videoRef}
          panelId={panelId.current}
        />
      </div>
    );
  };

  const getPanelContentForMobilePanel = (): ReactElement => {
    if (props.permissionsDeniedError || props.rempromptForPermissions || props.checkSystemPermissionsError) {
      return (
        <div className={styles.content}>
          <div className={`_fit-width ${styles.floatingCloseIcon} ${props.showMaxDurationError ? styles.floatingCloseIconLower : ''}`}>
            <Icon onClick={customOnClose} icon="icon-close" size={IconSize.SIZE_ICON_20} shape={IconShape.CIRCLE} type={IconType.DARK} />
          </div>
          <CameraAndMicrophonePermission
            panelId={panelId.current}
            rempromptForPermissions={props.rempromptForPermissions}
            checkSystemPermissionsError={props.checkSystemPermissionsError}
            permissionsDeniedError={props.permissionsDeniedError}
            onReprompt={isSafari ? setAllVideoInputDeviceIds : getCamera}
          />
          {recorderStatus === ImageAndVideoRecorderStatus.READY_TO_RECORD || recorderStatus === ImageAndVideoRecorderStatus.RECORDING ? (
            <ImageAndVideoToggleMobile className={hideToggle ? '_invisible' : ''} />
          ) : null}
          {recorderStatus === ImageAndVideoRecorderStatus.STOPPED ? getCustomMobileFooter() : null}
        </div>
      );
    }

    return (
      <div className={styles.content}>
        <div className={`_fit-width ${styles.floatingCloseIcon} ${props.showMaxDurationError ? styles.floatingCloseIconLower : ''}`}>
          <Icon onClick={customOnClose} icon="icon-close" size={IconSize.SIZE_ICON_20} shape={IconShape.CIRCLE} type={IconType.DARK} />
        </div>
        {props.showMaxDurationError ? getMaxDurationErrorMessage() : null}
        <ImageAndVideoRecorderFeed
          showIosPlaybackVideoPreloader={showIosPlaybackVideoPreloader}
          setShowIosPlaybackVideoPreloader={setShowIosPlaybackVideoPreloader}
          isOnMobile={isOpenCustomImageAndVideoRecorderSmall(windowWidth)}
          duration={props.recordingDuration}
          stream={props.stream}
          videoRef={props.videoRef}
        />
        {recorderStatus === ImageAndVideoRecorderStatus.READY_TO_RECORD || recorderStatus === ImageAndVideoRecorderStatus.RECORDING ? (
          <div className={`_full-width ${styles.floatingRecorderControls}`}>
            <ImageAndVideoRecorderControls
              videoInputDevices={props.videoInputDevices}
              isOnMobile={isOpenCustomImageAndVideoRecorderSmall(windowWidth)}
              getCamera={getCamera}
              captureImage={captureImage}
              startRecordingVideo={startRecordingVideo}
              stopRecordingVideo={stopRecordingVideo}
              duration={props.recordingDuration}
              hideToggle={hideToggle}
            />
          </div>
        ) : null}
        {recorderStatus === ImageAndVideoRecorderStatus.READY_TO_RECORD || recorderStatus === ImageAndVideoRecorderStatus.RECORDING ? (
          <ImageAndVideoToggleMobile className={hideToggle ? '_invisible' : ''} />
        ) : null}
        {recorderStatus === ImageAndVideoRecorderStatus.STOPPED ? getCustomMobileFooter() : null}
      </div>
    );
  };

  const getCustomMobileFooter = (): ReactElement | null => {
    if (
      props.permissionsDeniedError ||
      props.rempromptForPermissions ||
      props.checkSystemPermissionsError ||
      recorderStatus === ImageAndVideoRecorderStatus.LOADING_RECORDER ||
      showIosPlaybackVideoPreloader
    ) {
      return null;
    }

    return (
      <div className={`_full-width flex-center spacing-p-l-3 spacing-p-r-3 ${styles.footerMobile}`}>
        <div className={`_full-width flex-justify-between ${styles.mobileFooterButtonsWrapper}`}>
          <PanelFooterPrimaryDefaultAction
            onClick={() => {
              props.onRetake();
            }}
            type={Type.SECONDARY}
            key="btn-record-again"
            text={mediaType === RecorderMediaType.IMAGE ? window.i18next.t('pmwjs_retake_photo') : window.i18next.t('pmwjs_retake_video')}
          />
          <PanelFooterPrimaryDefaultAction onClick={onAddToDesign} key="btn-add-to-design" text={window.i18next.t('pmwjs_add_to_design')} />
        </div>
      </div>
    );
  };

  const getMobileContent = (): ReactElement => {
    return <PanelContent className="_full-height">{getPanelContentForMobilePanel()}</PanelContent>;
  };

  const getGeneralContent = (): ReactElement => {
    return (
      <PanelContent
        className={`${props.permissionsDeniedError || props.rempromptForPermissions || props.checkSystemPermissionsError || recorderStatus === ImageAndVideoRecorderStatus.LOADING_RECORDER || props.showMaxDurationError ? '_full-height' : 'spacing-m-t-8'}`}
      >
        {getPanelContentForGeneralPanel()}
      </PanelContent>
    );
  };

  const getContent = (): ReactElement => {
    if (isOpenCustomImageAndVideoRecorderSmall(windowWidth)) {
      return getMobileContent();
    }
    return getGeneralContent();
  };

  return getContent();
}
